
import { notify,
  type SDK_Stack, type SDK_UpdateStackMutationVariables,
  clientConfig,
  getSdk,
  strings,
  text,
} from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { cloneDeep } from "lodash-es";
import { useRecoilRefresher_UNSTABLE, useRecoilValue, useSetRecoilState } from "recoil";
import {
  EditorIsClean,
  IsUploading,
} from "../state/atoms";
import { OwnerName } from "../state/authSelectors";
import {
  NavigationTypeAtom,
  StoryNamesAtom,
} from "../state/stackAtoms";
import {
  StackCfKeysSelector,
} from "../state/stackSelectors";
import { ShowTknsPanelAtom } from "../state/tknsAtoms";
import { track } from "../lib/metrics";
import { isUpgradeCode, returnCodeMessage } from "../lib/returnCodeMessage";

export function useStackApi() {
  const ownerName = useRecoilValue(OwnerName);
  const setEditorIsClean = useSetRecoilState(EditorIsClean);
  const setIsUploading = useSetRecoilState(IsUploading);
  const storyNamesAtom = useRecoilValue(StoryNamesAtom);
  const navigationTypeAtom = useRecoilValue(NavigationTypeAtom);
  const refreshStackFileNames =
    useRecoilRefresher_UNSTABLE(StackCfKeysSelector);
  const setShowTknsPanel = useSetRecoilState(ShowTknsPanelAtom);

  async function saveStackToDb(stack: SDK_Stack) {
    console.log("Saving stack to DB", stack);
    if (stack.code === "" || stack.code === text.errors.noStack) {
      console.log("Stack code is empty or new, not saving");
      return;
    }
    const client = new GraphQLClient(clientConfig.gqlWriteStack());
    const sdk = getSdk(client);
    if (stack.stackCfKey === strings.unset) return;
    const user = getAuth().currentUser;
    if (!user) return;
    const jwt = await getIdToken(user);
    if (!jwt) return;

    const clonedStack = cloneDeep(stack) as SDK_UpdateStackMutationVariables;
    clonedStack.jwt = jwt;
    clonedStack.storyName = storyNamesAtom[0] ? storyNamesAtom[0] : "";
    clonedStack.navigationType = navigationTypeAtom
      ? navigationTypeAtom
      : "tap";
    console.log("clonedStack to be saved useStackApi.ts:", clonedStack);
    sdk
      .UpdateStack(clonedStack)
      .then((res) => {
        if (res.updateStack?.success) {
          setIsUploading(false);
          setEditorIsClean(true);
        } else {
          // Numeric return code → friendly message; a free-tier wall (the
          // conversion moment) also opens the Plus card.
          const code = res.updateStack?.message;
          setIsUploading(false);
          setEditorIsClean(false);
          console.error(res);
          notify(returnCodeMessage(code));
          if (isUpgradeCode(code)) setShowTknsPanel(true);
        }
      })
      .catch((error) => {
        setIsUploading(false);
        setEditorIsClean(false);
        notify("Something went wrong. Your story has not been saved");
        console.error(error);
      });
  }

  async function deleteStackFromDb(stackCfKey: string) {
    const client = new GraphQLClient(clientConfig.gqlWriteStack());
    const sdk = getSdk(client);
    const user = getAuth().currentUser;
    if (!user) return;

    const jwt = await getIdToken(user);

    if (ownerName && jwt) {
      const gqlVars = { jwt, ownerName, stackCfKey };

      sdk
        .DeleteStack(gqlVars)
        .then((res) => {
          if (res.deleteStack?.success) {
            refreshStackFileNames();
            track(user.uid, 'story_deleted', { stackCfKey });
          } else {
            notify("There was an error.");
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  return { saveStackToDb, deleteStackFromDb };
}
